import {onMounted, ref, watch} from 'vue';
import type {ComponentPublicInstance} from 'vue';
import type {FetchError} from 'ofetch';
import type AutoComplete from 'primevue/autocomplete';
import {useAbortableUniversalFetch, useLocale, useLogger, useTranslation} from '#imports';
import {timeout} from '@malt/utils';
import useDefaultLocationSuggestions from '../composables/useDefaultLocationSuggestions';
import type {Location} from '../types/location';
import {mockedLocations} from '../utils/mockedLocations';

interface SelectEvent<T> {
    originalEvent: Event;
    value: T;
}

export type Layer = 'COUNTRY' | 'LOCALITY' | 'FULL_ADDRESS';

export type LayerRequest = {
    name: Layer;
    size?: number;
};

export function useLocationAutocomplete({
    remoteAllowed = false,
    remoteByCountry = false,
    layer = 'LOCALITY',
    remoteLabel,
}: {
    remoteAllowed: boolean;
    remoteByCountry: boolean;
    layer: Layer | LayerRequest[];
    remoteLabel?: string;
}) {
    const logger = useLogger();
    const {t} = useTranslation();
    const {locale} = useLocale();
    const [, countryCode] = locale.value?.split('-') ?? [];
    const {execute: fetchAndMaybeAbort} = useAbortableUniversalFetch();

    const locationDefaultSuggestions: Location[] = [];
    const searchLocationSuggestions = ref(false);
    const suggestionLoading = ref(false);

    const locationInput = ref<string | null>('');
    const locationSuggestions = ref<Location[]>([]);
    const locationRef = ref<ComponentPublicInstance<typeof AutoComplete> | null>(null);
    const selectedLocation = ref<Location | null>(null);

    const remoteSuggestionLabel = remoteLabel ?? (remoteByCountry ? t(`search.location.remote.${countryCode}` as any) : t(`search.location.remote`));

    const {getDefaultLocationSuggestions} = useDefaultLocationSuggestions();

    const hasLocality = typeof layer === 'string' ? layer === 'LOCALITY' : layer.some((l) => l.name === 'LOCALITY');
    const layersNames = typeof layer === 'string' ? [layer] : layer.map((l) => l.name);

    onMounted(async () => {
        const defaultSuggestions = (await getDefaultLocationSuggestions(
            layersNames,
            remoteAllowed ? remoteSuggestionLabel : null,
        )) as unknown as Location[];
        locationDefaultSuggestions.push(...defaultSuggestions);

        // Usefull when autocomplete locality is focus by default
        if (hasLocality) {
            locationSuggestions.value = [...locationDefaultSuggestions];
        }
    });

    watch(locationInput, () => {
        if (!locationInput.value) {
            selectedLocation.value = null;
        }
    });

    async function onLocationChange() {
        searchLocationSuggestions.value = true;
        // Dirty hack to trigger the complete callback when location is cleared
        await timeout(500);
        if (!locationInput.value) {
            const $input = locationRef.value?.$el.querySelector('input') as HTMLInputElement;
            $input.blur();
            $input.focus();
        }
    }

    function onLocationSelect($event: SelectEvent<Location>, preventOptionSelection?: boolean) {
        selectedLocation.value = $event.value;
        locationInput.value = selectedLocation.value?.formattedAddress || '';
        searchLocationSuggestions.value = false;
        if (preventOptionSelection) {
            $event.originalEvent.preventDefault();
        }
    }

    async function locationSearch() {
        suggestionLoading.value = true;

        if (!locationInput.value) {
            locationSuggestions.value = hasLocality ? [...locationDefaultSuggestions] : [];
            suggestionLoading.value = false;
            return;
        }

        if (locationInput.value.length <= 1) {
            locationSuggestions.value = [];
            suggestionLoading.value = false;
            return;
        }

        if (!searchLocationSuggestions.value) {
            suggestionLoading.value = false;
            return;
        }

        try {
            const response = await fetchAndMaybeAbort<Location[]>(`/search/api/location/autocomplete`, {
                baseURL: '/',
                method: 'POST',
                body: {
                    query: locationInput.value,
                    ...(typeof layer === 'string' ? {layer} : {layers: layer}),
                    languageTag: locale.value,
                    size: 5,
                    useBackendfocusPoint: true,
                },
            });
            if (response) {
                locationSuggestions.value = response;
            }
        } catch (err) {
            const error = err as FetchError;
            logger.error(error);

            // If dev mode and service is unavailable
            if (process.dev && error.statusCode === 502) {
                locationSuggestions.value = mockedLocations;
            }
        } finally {
            suggestionLoading.value = false;
        }
    }

    return {
        locationInput,
        locationSuggestions,
        suggestionLoading,
        locationRef,
        selectedLocation,
        onLocationChange,
        onLocationSelect,
        locationSearch,
        remoteSuggestionLabel,
    };
}
